/**
 * Fantasy Football collection + PDP shared behavior.
 * Loaded with `defer` from the FF collection and product templates only.
 * Event delegation only. No inline script, no fbq, no purchase-tracking
 * changes.
 *
 * 2026-09-19 CRO final additions (this file only handles collection-page
 * concerns; nothing here touches /cart or checkout):
 *  - The team-select change navigation now appends the current
 *    window.location.search (UTMs, ?team, etc.) to the destination PDP
 *    URL, so paid-traffic attribution survives a selector-triggered
 *    navigation.
 *  - wtfFfApplyTeamParam(): reads ?team=<product-handle> and, only if it
 *    matches a data-product-handle actually rendered on THIS page, moves
 *    that card to position #1 for this page view, re-anchors the LEAGUE8
 *    offer rows, and preselects the team in the selector. No match =
 *    no-op, normal order preserved. Never rewrites the URL.
 *  - 2026-09-26 hero v3 (Jeff R198): on a match, the product leaves the
 *    grid and two colour cards (colour A = the ad image's colour, colour
 *    B = White or the next valid colour) are built from
 *    /products/<handle>.js into [data-wtf-ff-hero] in the intro section.
 *    ADD TO CART uses Dawn's <product-form>. Any failure falls back to
 *    the card-to-position-1 behaviour.
 *  - Accessible review modal: open/close, Esc, backdrop click, focus
 *    trap, and focus return to the trigger button.
 */
(function () {
  'use strict';

  function wtfFfTrack(name, data) {
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish('wtf_ff:' + name, data);
      }
    } catch (e) {
      /* silent */
    }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, data);
      }
    } catch (e) {
      /* silent */
    }
  }
  window.wtfFfTrack = wtfFfTrack;

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }

  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-wtf-ff-team-select]');
    if (!select || !select.value) return;

    var position = select.selectedIndex;
    var selectedOption = select.options[position];

    wtfFfTrack('fantasy_team_select', {
      source_page: select.getAttribute('data-source') || '',
      from_team: select.getAttribute('data-from-team') || '',
      to_team: selectedOption ? selectedOption.textContent.trim() : '',
      product_url: select.value,
      position: position
    });

    var destination = select.value;
    if (window.location.search) {
      destination += window.location.search;
    }
    window.location.assign(destination);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-wtf-ff-copy]');
    if (!button) return;

    var code = button.getAttribute('data-wtf-ff-copy');
    var originalText = button.textContent;
    var wrapper = button.closest('.wtf-ff-league-offer');
    var status = wrapper ? wrapper.querySelector('.wtf-ff-league-offer__status') : null;

    copyText(code)
      .then(function () {
        button.textContent = 'Copied!';
        if (status) status.textContent = 'Copied ' + code + ' to clipboard.';
        wtfFfTrack('league8_copy', {
          source_page: button.getAttribute('data-source') || ''
        });
        window.setTimeout(function () {
          button.textContent = originalText;
          if (status) status.textContent = '';
        }, 2000);
      })
      .catch(function () {
        /* silent: clipboard permission denied or unsupported */
      });
  });

  /**
   * ?team=<product-handle>: preselect + move-to-front, validated against
   * the real product handles rendered on this page. Does not touch the
   * URL, canonicals, or Shopify's own collection sort.
   */
  /* CLS fix 2026-10-01: undo the skeleton that the inline script in the section reserved before first
     paint, and show the matched grid card again. Safe to call at any time. */
  function wtfFfClearPending() {
    var h = document.querySelector('[data-wtf-ff-hero]');
    if (h) {
      h.innerHTML = '';
      h.removeAttribute('data-wtf-skel');
      h.setAttribute('hidden', '');
      h.classList.remove('wtf-ff-hero--single');
      var inr = h.closest('.wtf-ff-coll-intro__inner');
      if (inr) inr.classList.remove('wtf-ff-coll-intro__inner--hero');
    }
    var s = document.getElementById('wtf-ff-team-pending-style');
    if (s) s.remove();
  }

  function wtfFfApplyTeamParam() {
    var grid = document.querySelector('.wtf-ff-grid__list');
    if (!grid) { wtfFfClearPending(); return; }

    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var team = params.get('team');
    if (!team) return;
    team = team.trim().toLowerCase();
    if (!team) return;

    var items = Array.prototype.slice.call(grid.children).filter(function (li) {
      return li.hasAttribute('data-product-handle');
    });
    var match = null;
    for (var i = 0; i < items.length; i++) {
      if ((items[i].getAttribute('data-product-handle') || '').toLowerCase() === team) {
        match = items[i];
        break;
      }
    }
    if (!match) { wtfFfClearPending(); return; } // invalid/unknown slug: ignore, keep normal order

    var handle = match.getAttribute('data-product-handle');
    var hero = document.querySelector('[data-wtf-ff-hero]');
    var inner = hero ? hero.closest('.wtf-ff-coll-intro__inner') : null;
    var tpl = document.querySelector('template[data-wtf-ff-hero-card]');

    // Re-anchor the LEAGUE8 offer rows after the (possibly new) 4th / 6th
    // product card so "after the first 4" still holds post-reorder.
    function wtfFfReanchorOffers() {
      var afterFour = grid.querySelector('.wtf-ff-grid__offer-item--after4');
      var afterSix = grid.querySelector('.wtf-ff-grid__offer-item--after6');
      var productItems = Array.prototype.slice.call(grid.children).filter(function (li) {
        return li.hasAttribute('data-product-handle');
      });
      if (afterFour && productItems[3] && productItems[3].nextSibling !== afterFour) {
        grid.insertBefore(afterFour, productItems[3].nextSibling);
      }
      if (afterSix && productItems[5] && productItems[5].nextSibling !== afterSix) {
        grid.insertBefore(afterSix, productItems[5].nextSibling);
      }
    }

    // Preselect the team in the selector.
    function wtfFfPreselect(h) {
      var select = document.querySelector('[data-wtf-ff-team-select]');
      if (select && h) {
        for (var j = 0; j < select.options.length; j++) {
          var o = select.options[j];
          if (o.value && o.value.split('/').pop() === h) {
            select.value = o.value;
            break;
          }
        }
      }
    }

    function wtfFfFallback() {
      // Today's behaviour, unchanged: card to position 1.
      wtfFfClearPending();
      grid.insertBefore(match, grid.firstChild);
      wtfFfReanchorOffers();
      wtfFfPreselect(handle);
      wtfFfTrack('fantasy_team_param_applied', { team_param: team, matched_handle: handle || '' });
    }

    if (!hero || !inner || !tpl || !window.fetch) { wtfFfFallback(); return; }

    // Hero v3 (Jeff R198): two colour cards from the product JSON, with
    // ADD TO CART through Dawn's <product-form>.
    var early = window.__wtfFfHeroFetch;
    (early && early.handle === handle ? early.p : fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } }))
      .then(function (r) { if (!r.ok) throw new Error('product json ' + r.status); return r.json(); })
      .then(function (p) {
        var ci = p.options.findIndex(function (o) { return /colou?r/i.test(o.name); });
        var si = p.options.findIndex(function (o) { return /size/i.test(o.name); });
        var sti = p.options.findIndex(function (o) { return /style/i.test(o.name); });
        if (ci < 0 || si < 0) throw new Error('no colour/size option');
        var opt = function (v, i) { return v['option' + (i + 1)]; };
        var vars = p.variants;
        if (sti >= 0) {
          var firstStyle = p.options[sti].values[0];
          vars = vars.filter(function (v) { return opt(v, sti) === firstStyle; });
        }
        if (!vars.length) throw new Error('no variants');
        var norm = function (u) { return (u || '').replace(/^\/\//, 'https://').split('?')[0]; };
        var feat = norm(p.featured_image);
        var hasImg = function (v) { return v.featured_image && v.featured_image.src; };
        var vA = vars.filter(hasImg).find(function (v) { return norm(v.featured_image.src) === feat; }) || vars[0];
        var A = opt(vA, ci);
        var ok = function (c) {
          return c !== A && vars.some(function (v) {
            return opt(v, ci) === c && v.available && hasImg(v) && norm(v.featured_image.src) !== feat;
          });
        };
        var others = p.options[ci].values.filter(ok);
        var B = others.indexOf('White') >= 0 ? 'White' : (others[0] || null);

        var money = function (c) { return '$' + (c / 100).toFixed(2); };
        var pdpUrl = function (variantId) {
          var q = new URLSearchParams(window.location.search);
          q.set('variant', variantId);
          q.delete('team');
          return '/products/' + p.handle + '?' + q.toString();
        };
        var sized = function (src, w) { return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w; };

        function buildCard(colour, isA) {
          var vs = vars.filter(function (v) { return opt(v, ci) === colour; });
          var first = vs.find(function (v) { return v.available; }) || vs[0];
          var frag = tpl.content.cloneNode(true);
          var card = frag.querySelector('.wtf-ff-hero__card');
          card.classList.add(isA ? 'wtf-ff-hero__card--a' : 'wtf-ff-hero__card--b');
          card.setAttribute('data-wtf-hero-colour', colour);
          if (isA) card.querySelector('[data-badge]').removeAttribute('hidden');
          var src = (first.featured_image && first.featured_image.src) || p.featured_image;
          var img = card.querySelector('[data-img]');
          img.src = sized(src, 720);
          img.srcset = [360, 533, 720, 940].map(function (w) { return sized(src, w) + ' ' + w + 'w'; }).join(', ');
          img.sizes = '(min-width: 990px) 300px, (min-width: 750px) 368px, calc(50vw - 20px)';
          img.alt = p.title + ' in ' + colour;
          card.querySelector('[data-title]').textContent = p.title;
          var links = card.querySelectorAll('[data-link]');
          links.forEach(function (a) { a.href = pdpUrl(first.id); });
          card.querySelector('[data-colour]').textContent = colour;
          var priceEl = card.querySelector('[data-price]');
          var compEl = card.querySelector('[data-compare]');
          var showPrice = function (v) {
            priceEl.textContent = money(v.price);
            if (v.compare_at_price && v.compare_at_price > v.price) {
              compEl.textContent = money(v.compare_at_price);
              compEl.removeAttribute('hidden');
            } else {
              compEl.setAttribute('hidden', '');
            }
          };
          showPrice(first);
          var sel = card.querySelector('[data-size]');
          var atc = card.querySelector('[data-atc]');
          var selId = 'WtfFfHeroSize-' + (isA ? 'a' : 'b');
          sel.id = selId;
          card.querySelector('[data-size-label]').setAttribute('for', selId);
          vs.forEach(function (v) {
            var o = document.createElement('option');
            o.value = String(v.id);
            o.textContent = opt(v, si) + (v.available ? '' : ' (sold out)');
            if (!v.available) o.disabled = true;
            sel.appendChild(o);
          });
          sel.addEventListener('change', function () {
            var v = vs.find(function (x) { return String(x.id) === sel.value; });
            if (v) {
              showPrice(v);
              atc.setAttribute('aria-disabled', 'false');
              links.forEach(function (a) { a.href = pdpUrl(v.id); });
            } else {
              atc.setAttribute('aria-disabled', 'true');
            }
          });
          // product-form.js ignores a submit while aria-disabled="true"; this
          // keeps an empty size from posting and sends focus to the select.
          atc.addEventListener('click', function (e) {
            if (!sel.value || atc.getAttribute('aria-disabled') === 'true') { e.preventDefault(); sel.focus(); }
          });
          return frag;
        }

        hero.innerHTML = ''; // drop the skeleton in the same task as the real cards go in (no paint between)
        hero.removeAttribute('data-wtf-skel');
        hero.classList.remove('wtf-ff-hero--single'); // the real colour count decides, not the skeleton guess
        hero.appendChild(buildCard(A, true));
        if (B) hero.appendChild(buildCard(B, false));
        else hero.classList.add('wtf-ff-hero--single');
        hero.removeAttribute('hidden');
        inner.classList.add('wtf-ff-coll-intro__inner--hero');
        grid.removeChild(match);
        wtfFfReanchorOffers();
        wtfFfPreselect(handle);
        wtfFfTrack('fantasy_team_param_applied', { team_param: team, matched_handle: handle || '' });
        wtfFfTrack('as_seen_hero', { handle: handle, colour_a: A, colour_b: B || '' });
      })
      .catch(function () {
        // Any failure: clear a half-built hero, then today's behaviour.
        hero.innerHTML = '';
        hero.removeAttribute('data-wtf-skel');
        var pst = document.getElementById('wtf-ff-team-pending-style');
        if (pst) pst.remove();
        hero.setAttribute('hidden', '');
        hero.classList.remove('wtf-ff-hero--single');
        inner.classList.remove('wtf-ff-coll-intro__inner--hero');
        if (!match.parentNode) grid.appendChild(match);
        wtfFfFallback();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wtfFfApplyTeamParam);
  } else {
    wtfFfApplyTeamParam();
  }

  /**
   * Accessible review modal: role="dialog" panel already in the markup.
   * This wires open/close, Esc, backdrop click, a focus trap, and focus
   * return to the trigger button that opened it.
   */
  var wtfFfActiveModal = null;
  var wtfFfActiveTrigger = null;

  function wtfFfFocusableEls(container) {
    var selector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(container.querySelectorAll(selector)).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function wtfFfOpenModal(modal, trigger) {
    if (!modal) return;
    modal.hidden = false;
    wtfFfActiveModal = modal;
    wtfFfActiveTrigger = trigger || document.activeElement;

    var panel = modal.querySelector('[role="dialog"]');
    if (panel) {
      panel.focus();
    }

    document.addEventListener('keydown', wtfFfHandleModalKeydown, true);
    wtfFfTrack('fantasy_reviews_modal_open', {});
  }

  function wtfFfCloseModal() {
    if (!wtfFfActiveModal) return;
    wtfFfActiveModal.hidden = true;
    document.removeEventListener('keydown', wtfFfHandleModalKeydown, true);

    if (wtfFfActiveTrigger && typeof wtfFfActiveTrigger.focus === 'function') {
      wtfFfActiveTrigger.focus();
    }
    wtfFfActiveModal = null;
    wtfFfActiveTrigger = null;
  }

  function wtfFfHandleModalKeydown(event) {
    if (!wtfFfActiveModal) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      wtfFfCloseModal();
      return;
    }

    if (event.key !== 'Tab') return;

    var panel = wtfFfActiveModal.querySelector('[role="dialog"]');
    if (!panel) return;
    var focusable = wtfFfFocusableEls(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-wtf-ff-reviews-trigger]');
    if (trigger) {
      var modalId = trigger.getAttribute('aria-controls');
      var modal = modalId ? document.getElementById(modalId) : null;
      if (modal) {
        wtfFfOpenModal(modal, trigger);
      }
      return;
    }

    var closer = event.target.closest('[data-wtf-ff-reviews-close]');
    if (closer) {
      wtfFfCloseModal();
    }
  });
})();
